import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

export default class extends Controller {
  static targets = ["canvas", "pageNum", "pageCount", "prevBtn", "nextBtn", "zoomDisplay", "loading", "error"]
  static values = { url: String, workerUrl: String, scale: { type: Number, default: 1.5 } }

  connect() {
    pdfjsLib.GlobalWorkerOptions.workerSrc = this.workerUrlValue
    this.currentPage = 1
    this.totalPages = 0
    this.pdfDoc = null
    this.renderTask = null
    this.loadPDF()
  }

  disconnect() {
    if (this.renderTask) this.renderTask.cancel()
    if (this.pdfDoc) this.pdfDoc.destroy()
  }

  async loadPDF() {
    try {
      this.pdfDoc = await pdfjsLib.getDocument(this.urlValue).promise
      if (!this.element.isConnected) return
      this.totalPages = this.pdfDoc.numPages
      this.pageCountTarget.textContent = this.totalPages
      this.loadingTarget.classList.add("hidden")
      this.canvasTarget.classList.remove("hidden")
      await this.renderPage(this.currentPage)
    } catch (error) {
      if (!this.element.isConnected) return
      console.error("PDF load error:", error)
      this.loadingTarget.classList.add("hidden")
      this.errorTarget.classList.remove("hidden")
    }
  }

  async renderPage(pageNum) {
    if (this.renderTask) {
      this.renderTask.cancel()
      this.renderTask = null
    }

    try {
      const page = await this.pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: this.scaleValue })
      const canvas = this.canvasTarget
      canvas.width = viewport.width
      canvas.height = viewport.height

      this.renderTask = page.render({ canvasContext: canvas.getContext("2d"), viewport })
      await this.renderTask.promise

      this.pageNumTarget.textContent = pageNum
      this.zoomDisplayTarget.textContent = `${Math.round(this.scaleValue * 100)}%`
      this.updateButtons()
    } catch (error) {
      if (error.name !== "RenderingCancelledException") console.error("PDF render error:", error)
    }
  }

  prevPage() {
    if (this.currentPage <= 1) return
    this.currentPage--
    this.renderPage(this.currentPage)
  }

  nextPage() {
    if (this.currentPage >= this.totalPages) return
    this.currentPage++
    this.renderPage(this.currentPage)
  }

  zoomIn() {
    this.scaleValue = Math.min(this.scaleValue + 0.25, 3.0)
    this.renderPage(this.currentPage)
  }

  zoomOut() {
    this.scaleValue = Math.max(this.scaleValue - 0.25, 0.5)
    this.renderPage(this.currentPage)
  }

  updateButtons() {
    this.prevBtnTarget.disabled = this.currentPage <= 1
    this.nextBtnTarget.disabled = this.currentPage >= this.totalPages
  }
}
